<template>
  <div class="padded">

    <edit-pane v-if="!hidePanel" ref="cd" :title="form.name"
    :is-edit="editModeState" :prefix="prefix" :show-edit-button="isAdding || profile().login === form.login || isSuper() || isManager('admin|management')"
    :parent-managed="parentManaged"
    :self-editable="!isAdding && profile().login === form.login"
    @save="onSave" @cancel="onCancel" @edit="onEdit" @add="onAdd"
    :show-background="!isAdding"  />

    <el-form ref="contactDetailsForm" label-width="90px">

      <el-tabs type="border-card"
        :active-name="currentTab"
        :before-leave="validateForm"
        @tab-click="tabChanged">
        <el-tab-pane name="contact" label="Contact">

          <el-row>
            <el-form-item label="Name" required>
              <el-input name="name" required v-model="form.name" :disabled="disabled" placeholder="Full Name" />
            </el-form-item>

            <el-form-item label="Short Name">
              <el-input name="shortName" v-model="form.shortName" :disabled="disabled" placeholder="Short Name" />
            </el-form-item>

            <el-form-item label="Job Title">
              <el-input name="jobTitle" v-model="form.jobTitle" :disabled="disabled" />
            </el-form-item>

          </el-row>

          <el-row>
            <el-form-item label="Phone">
              <el-input type="phone" name="phone" v-model="form.phone" :disabled="disabled" />
            </el-form-item>
            <el-form-item label="E-mail">
              <el-input type="email" name="email" v-model="form.email" :disabled="disabled" />
            </el-form-item>
          </el-row>

          <el-row>
            <el-col :span="12">
              <check-box label="Upload Alerts" v-model="form.sendUploadAlerts" :disabled="disabled" />
            </el-col>

            <el-col :span="12">
              <check-box label="Approval Alerts" v-model="form.sendApprovalAlerts" :disabled="disabled" />
            </el-col>
          </el-row>

          <!-- XOL-371: a user may change their OWN password regardless of edit
               mode / view-only status (self credential rotation; API keeps
               /password/change exempt from the view-only write-block). Only the
               own-record case is unlocked here; managing another contact's
               password stays gated by edit mode.

               XOL-391: only offer the password control when the contact
               actually has a login account to set a password on — a login id
               AND the account enabled. A contact with no login (the default for
               a new contact, and for the 88 login-less contacts in the data) has
               no credential to rotate, so the button was meaningless there. -->
          <password-dialog v-if="hasLoginAccount" class="text-right" v-model="form" change :new-psw="isAdding" :disabled="disabled && profile().login !== form.login" />

        </el-tab-pane>





        <!-- XOL-391: hidden while ADDING (pending XOL-392). Every field on this
             tab — login, enabled, defaultView, userRole, viewer/approver/
             manager/super — is in ContactsController::SUPER_ONLY_FIELDS, which
             store() strips for any non-super caller, so a manager filling it in
             on create has the input silently discarded. Setting a password at
             create time is disabled for the same reason, and configuring a
             login here without being able to set its password is a dead end.
             Create the contact first, then configure it on the saved record. -->
        <el-tab-pane name="systemDetails" v-if="!isAdding && isSuper() && isTowerCo()" label="System Details">
          <el-row :gutter="12">

            <el-form label-position="top" :inline="true">
              <el-row>
              <el-col :span="24">
                <switch-input
                  label="Enabled?"
                  on-text="Enabled"
                  off-text="Disabled"
                  v-model="form.enabled"
                  :disabled="disabled" />

                <text-input
                label="Login ID"
                 v-model="form.login"
                 placeholder="Login Id"
                :disabled="disabled" />

              </el-col>
            </el-row>

              <section  v-if="form.orgCategory === 'Tower Company'">
                <el-row :gutter="5.28">

                <el-col :xs="24" :sm="24" :md="12" :lg="12">
                  <select-input :list="roles" v-model="form.defaultView" label="Default View"  :disabled="disabled" />
                </el-col>

                <el-col :xs="24" :sm="24" :md="12" :lg="12">

                  <el-transfer
                      v-if="!disabled"
                      v-model="formUserRoles"
                      :titles="['All Roles', 'User Roles']"
                      :data="roles"
                      @change="changeUserRoles" />

                  <el-form-item  v-else label="User Roles">
                    <span  v-for="(role, i) in roles" :key="i">
                      <el-tag v-if="formUserRoles.includes(role.value)" :type="role.type">
                        {{role.label}}
                      </el-tag>&nbsp;
                    </span>
                  </el-form-item>

                </el-col>

              </el-row>

              <el-row :gutter="5.28">

                <el-col :xs="24" :sm="24" :md="12" :lg="6">

                  <switch-input label="View Only" v-model="form.viewer" :disabled="disabled" />

                  <el-alert
                    title="View Only"
                    type="info"
                    description="View Only can't add, edit or delete anything. Only can create tickets."
                    show-icon
                    :closable="false" />
                </el-col>

                <el-col :xs="24" :sm="24" :md="12" :lg="6">

                  <switch-input label="Approver" v-model="form.approver" :disabled="disabled" />

                  <el-alert
                     title="Requests Module Only"
                     type="success"
                     description="Approver for Requests (can approve and send approval email)"
                     show-icon
                     :closable="false" />

                </el-col>

                <el-col :xs="24" :sm="24" :md="12" :lg="6">

                  <switch-input label="Manager" v-model="form.manager" :disabled="disabled" />

                  <el-alert
                    title="Manager"
                    type="warning"
                    description="Manager can add, edit and delete according to role(s)"
                    show-icon
                    :closable="false" />
                </el-col>

                <el-col :xs="24" :sm="24" :md="12" :lg="6">

                  <switch-input label="Super" v-model="form.super" :disabled="disabled" />

                  <el-alert
                    title="Super User"
                    type="error"
                    description="Super User can create users and delete anything even sites."
                    show-icon
                    :closable="false" />
                </el-col>
              </el-row>
            </section>

          </el-form>

        </el-row>

      </el-tab-pane>

      <el-tab-pane name="remarks" v-if="isManager() || isSuper()" label="Remarks">

          <html-input v-model="form.remarks"
            label="Remarks"
            :show-empty-message="true"
            :disabled="disabled" />

      </el-tab-pane>

    </el-tabs>
  </el-form>

  <small v-if="!isAdding" class="text-muted">ID <b>{{form.organisation_id + '-' + form.id + '-' + form.created_at}}</b></small>
</div>

</template>


<script>

import axios from '@/lib/axiosClient'
import SiteMixins from '@/mixins/SiteMixins'
import PasswordDialog from '@/components/login/PasswordDialog'

export default {

  mixins: [SiteMixins],


  components: {
    PasswordDialog
  },



  props: {
    rowData: {
      type: Object,
      required: true
    },

    prefix: {
      type: String,
      default: 'Contact'
    },

    orgId: {
      type: Number
    },

    isDisabled: {
      type: Boolean,
      default: true
    },

    editMode: {
      type: Boolean,
      default: false
    },

    tableRef: {
      type: String,
      default: 'contacts-table'
    },

    adding: {
      type: Boolean,
      default: false
    },

    hidePanel: {
      type: Boolean,
      default: false
    },

    parentManaged: {
      type: Boolean,
      default: true
    }

  },



  computed: {

    // XOL-391: gates the password control. Two conditions.
    //
    // 1. Not while ADDING. Setting a credential during create is deliberately
    //    disabled pending XOL-392 — the create path and the change path do not
    //    agree on password handling: PUT /password/change enforces min:8 and a
    //    self-or-super check, while POST /contacts applies no password rule at
    //    all, and for a non-super caller it strips `login`/`enabled` from the
    //    payload while KEEPING `password` — which would store a credential
    //    against a contact that has no login id and cannot sign in. Create the
    //    contact first, then set the password on the saved record.
    //
    // 2. The contact must actually have a login account — a login id present
    //    AND the account enabled — or the control has nothing to act on.
    hasLoginAccount () {
      if (this.isAdding) {
        return false
      }

      return !!(this.form && this.form.login && this.form.enabled)
    }

  },


  methods: {

    changeUserRoles (roles) {
      let userRole = ''

      if (roles.length > 0) {

         for (let s in roles) {
             userRole += (s > 0 ? '|' + roles[s] : roles[s])
           }

         } else {
           userRole = this.formUserRoles[0]
         }

     this.form.userRole = userRole
    },



    resetData () {
      this.form.id = null
      this.form.name = null
      this.form.jobTitle = null
      this.form.phone = null
      this.form.email = null
      this.form.enabled = false
      this.form.login = null
      this.form.sendUploadAlerts = false
      this.form.sendApprovalAlerts = false
    },



    success (response) {
      this.initialForm = { ...this.form }
      this.$events.emit(this.tableRef + '-refresh-table')
    },



    onCancel () {
      this.disabled = true
      if (this.parentManaged) this.editModeState = false

      // Blur the active element if it belongs to this form before resetting.
      // Rationale: after a blocked tab-switch, the browser can latch an
      // invalid required <input> into a visual state where a subsequent
      // programmatic value assignment (via Element UI's nativeInputValue
      // watcher) is not repainted. Releasing focus clears the latch.
      // See .context/plans/contact-detail-stale-input-plan.md.
      const formEl = this.$refs.contactDetailsForm && this.$refs.contactDetailsForm.$el
      const active = typeof document !== 'undefined' ? document.activeElement : null
      if (formEl && active && formEl.contains(active) && typeof active.blur === 'function') {
        active.blur()
      }

      this.$nextTick(() => {
        this.form = { ...this.initialForm }
      })
    },



    onEdit () {
      this.disabled = false
      if (this.parentManaged) this.editModeState = true
    },



    onAdd () {
      this.disabled = false
      this.isAdding = true

      var id = this.form.organisation_id
      this.form = this.getTableStruct('contacts')
      this.form.organisation_id = id
      if (this.parentManaged) this.editModeState = true
    },


    validateForm () {
      const form = this.$refs.contactDetailsForm.$el
      if (!form.checkValidity()) {
        form.reportValidity()
        return false
      }
      return true
    },

    onSave (orgId) {
      if (!this.validateForm()) {
        if (this.parentManaged) this.editModeState = true
        return
      }

      if (orgId && this.isAdding) {
        this.org_id = orgId
        this.form.organisation_id = this.org_id
      }

      if (this.form.sendUploadAlerts || this.form.sendApprovalAlerts) {

        if (this.isNull(this.form.sendUploadAlerts)) {
          this.form.sendUploadAlerts = false
        }

        if (this.isNull(this.form.sendApprovalAlerts)) {
          this.form.sendApprovalAlerts = false
        }

      }

      this.disabled = this.isAdding ? false : true
      if (this.parentManaged) this.editModeState = false

      // XOL-371: a contact editing their OWN record saves to the self-service
      // profile endpoint (server derives the user from the JWT, allow-list
      // fields only). Managing another contact, or adding one, keeps the
      // existing /contacts resource save.
      if (!this.isAdding && this.profile().login === this.form.login) {
        this.saveSelfProfile()
      } else {
        this.saveForm(this, '/contacts', this.form.id, this.contactPayload(), this.form.name, this.success)
      }
    },


    // XOL-391: the API validates `login` with `sometimes|unique:contacts,login`.
    // `sometimes` only skips a key that is ABSENT — sending `login: null` still
    // runs the unique check, and Laravel turns a null value into
    // `WHERE login IS NULL`, which matches every login-less contact already in
    // the table. Creating a contact without a login therefore failed with a
    // bogus "The login has already been taken." (resetData() sets login to
    // null, so this hit every new contact that wasn't given a login id).
    // Omit the key entirely when there is no login — exactly what `sometimes`
    // is for — so the column keeps its NULL default instead of an empty string.
    // Only the create path validates login this way (update() takes a plain
    // Request, not ContactsRequest), but the payload is built the same way for
    // both so an empty login never means "clear this field to ''".
    contactPayload () {
      var payload = { ...this.form }

      if (payload.login === null || payload.login === undefined || payload.login === '') {
        delete payload.login
      }

      // XOL-391: the /contacts resource never carries a credential. Password
      // changes go through PUT /password/change/{id}, which enforces min:8 and
      // a self-or-super check; POST /contacts applies no password rule at all,
      // and for a non-super caller strips `login`/`enabled` while KEEPING
      // `password`, which would store a credential against a contact that has
      // no login id and cannot sign in. Setting a password during create is
      // deferred to XOL-392; until then keep the boundary explicit here rather
      // than relying on the form never holding a value.
      //
      // `c_password` is the confirm-field companion PasswordDialog seeds onto
      // the shared form — not a contacts column, never sendable either way.
      delete payload.password
      delete payload.c_password

      return payload
    },


    // XOL-371: PUT /api/profile — self-service save of the logged-in
    // contact's own record. Own record only (server: contact.id === jwt.sub);
    // send ONLY the self-editable fields (no role/system fields). Mirrors
    // saveForm()'s use of PostMixins' send() for success/error handling.
    // Paired endpoint: ProfileController@update in xolas-tms-api (XOL-371).
    saveSelfProfile () {
      var payload = {
        name: this.form.name,
        shortName: this.form.shortName,
        jobTitle: this.form.jobTitle,
        phone: this.form.phone,
        email: this.form.email,
        password: this.form.password,
        sendUploadAlerts: this.form.sendUploadAlerts,
        sendApprovalAlerts: this.form.sendApprovalAlerts
      }

      // allowViewOnly=true (last arg): /profile is the one self-service surface a
      // view-only user may write; the API keeps PUT /api/profile exempt too.
      this.send(this, this.getEndpoint('profile', null, false), payload, this.form.name, this.success, null, axios.put, true, 'Save', null, false, true)
    },


    tabChanged (pane) {
      this.currentTab = pane.name
    },



    onClose () {
      this.resetData()
    }

  },



  data () {
    const form = this.adding ? this.getTableStruct('contacts') : this.rowData

    let formUserRoles = []
    if (form.userRole) {
      formUserRoles = form.userRole.trim().split('|')
    }

    return {
      disabled: this.isBoolean(this.isDisabled) ? this.isDisabled : true,
      emailDisabled: !(this.adding && this.isManager() || this.profile().login === this.rowData.login),
      isAdding: this.adding,
      formUserRoles: formUserRoles,
      roles: [
       { key: 'project', label: 'Project', value: 'project', type: 'primary' },
       { key: 'property', label: 'Property', value: 'property', type: 'secondary' },
       { key: 'maintenance', label: 'Maintenance', value: 'maintenance', type: 'warning' },
       { key: 'finance', label: 'Finance', value: 'finance', type: 'success' },
       { key: 'admin', label: 'Admin', value: 'admin', type: 'info' },
       { key: 'management', label: 'Management', value: 'management', type: 'primary' }
      ],
      org_id: (this.rowData.organisation_id ? this.rowData.organisation_id : this.orgId),
      form: form,
      initialForm: { ...form },
      editModeState: this.editMode,
      currentTab: 'contact'
    }
  },

  created () {

    if (this.isAdding) {
      this.disabled = false
      delete this.form.id
    }

    if (!this.form.organisation_id) {
      this.form.organisation_id = this.org_id
    }

  }

}

</script>
